import React, { memo, useEffect, useState } from "react";
import { Card, Flex, Image, Typography } from "antd";
import { FileOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { RootState } from "../../../redux/store";
import { checkIfContactSaved, formatTimestamp } from "../../../utils";
import { storageService } from "../../../services/storageService";
import useUploadProgress from "../../../hooks/useUploadProgress";
import uploadManager from "../../../services/uploadManager";
import AudioPlayer from "../../common/AudioPlayer";
import MsgActionsMenu from "../MsgActionsMenu";
import ScheduleBubble from "../ScheduleBubble";
import PollBubble from "../PollBubble";
import EmojiRenderer from "../../common/EmojiRenderer";
import "./index.scss";

const { Text } = Typography;

// ── Upload overlay (images / documents) ────────────────────────────────────
const OVL_SIZE = 48;
const OVL_STROKE = 3;
const OVL_R = (OVL_SIZE - OVL_STROKE) / 2;
const OVL_C = 2 * Math.PI * OVL_R;

const UploadOverlay: React.FC<{
  taskId: string;
  status: string;
  progress: number;
}> = ({ taskId, status, progress }) => {
  const offset = OVL_C - (progress / 100) * OVL_C;

  if (status === "sent") return null;

  return (
    <div className="upload-overlay">
      <div className="upload-progress-ring">
        <svg width={OVL_SIZE} height={OVL_SIZE}>
          <circle
            className="ring-bg"
            cx={OVL_SIZE / 2}
            cy={OVL_SIZE / 2}
            r={OVL_R}
            strokeWidth={OVL_STROKE}
          />
          {status === "uploading" && (
            <circle
              className="ring-fg"
              cx={OVL_SIZE / 2}
              cy={OVL_SIZE / 2}
              r={OVL_R}
              strokeWidth={OVL_STROKE}
              strokeDasharray={OVL_C}
              strokeDashoffset={offset}
            />
          )}
        </svg>
        {status === "uploading" && (
          <button
            className="overlay-action"
            onClick={(e) => {
              e.stopPropagation();
              uploadManager.cancel(taskId);
            }}
            title="Cancel upload"
          >
            <CloseOutlined />
          </button>
        )}
        {status === "failed" && (
          <button
            className="overlay-action retry"
            onClick={(e) => {
              e.stopPropagation();
              uploadManager.retry(taskId);
            }}
            title="Retry upload"
          >
            <ReloadOutlined />
          </button>
        )}
      </div>
      {status === "uploading" && progress > 0 && (
        <span className="upload-percent">{progress}%</span>
      )}
      {status === "failed" && (
        <span className="upload-percent failed">Failed</span>
      )}
    </div>
  );
};

// ── Inline audio upload indicator (small ring, right-aligned) ──────────────
const AUDIO_RING = 28;
const AUDIO_STROKE = 2.5;
const AUDIO_R = (AUDIO_RING - AUDIO_STROKE) / 2;
const AUDIO_C = 2 * Math.PI * AUDIO_R;

const AudioUploadIndicator: React.FC<{
  taskId: string;
  status: string;
  progress: number;
}> = ({ taskId, status, progress }) => {
  const offset = AUDIO_C - (progress / 100) * AUDIO_C;

  if (status === "sent") return null;

  return (
    <div className="audio-upload-indicator">
      <div className="audio-ring-wrap">
        <svg width={AUDIO_RING} height={AUDIO_RING}>
          <circle
            className="ring-bg"
            cx={AUDIO_RING / 2}
            cy={AUDIO_RING / 2}
            r={AUDIO_R}
            strokeWidth={AUDIO_STROKE}
          />
          {status === "uploading" && (
            <circle
              className="ring-fg"
              cx={AUDIO_RING / 2}
              cy={AUDIO_RING / 2}
              r={AUDIO_R}
              strokeWidth={AUDIO_STROKE}
              strokeDasharray={AUDIO_C}
              strokeDashoffset={offset}
            />
          )}
        </svg>
        {status === "uploading" && (
          <button
            className="audio-ring-action"
            onClick={(e) => {
              e.stopPropagation();
              uploadManager.cancel(taskId);
            }}
            title="Cancel"
          >
            <CloseOutlined style={{ fontSize: 10 }} />
          </button>
        )}
        {status === "failed" && (
          <button
            className="audio-ring-action"
            onClick={(e) => {
              e.stopPropagation();
              uploadManager.retry(taskId);
            }}
            title="Retry"
          >
            <ReloadOutlined style={{ fontSize: 10 }} />
          </button>
        )}
      </div>
      {status === "failed" && (
        <span className="audio-upload-label failed">Failed</span>
      )}
    </div>
  );
};

interface ChatBubbleProps {
  msg: any;
  isUser: boolean;
  setReplyingTo: any;
  showchatDeleteModal: (id: string) => void;
  scrollToMessage?: (id: string) => void;
  onMediaClick?: (msgId: string | number) => void;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (msg: any) => void;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  msg,
  isUser,
  setReplyingTo,
  showchatDeleteModal,
  scrollToMessage,
  onMediaClick,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const [showScheduleBubble, setShowScheduledBubble] = useState<boolean>(false);
  const upload = useUploadProgress(msg?.uploadTaskId);

  const isUploading =
    msg?.uploadTaskId &&
    (upload.status === "uploading" || upload.status === "pending");
  const isUploadFailed = msg?.uploadTaskId && upload.status === "failed";

  useEffect(() => {
    if (!msg?.scheduleTime || !msg.isUser) {
      setShowScheduledBubble(false);
      return;
    }

    const scheduledTime = moment(msg.scheduleTime);
    const now = moment();

    setShowScheduledBubble(scheduledTime.isAfter(now));
  }, [msg.isUser, msg.scheduleTime]);

  return (
    <Card
      className={`chat-bubble ${isUser ? "chat-bubble-user" : "chat-bubble-other"}${isSelected ? " chat-bubble-selected" : ""}`}
      bordered={false}
    >
      {showScheduleBubble ? (
        <ScheduleBubble
          date={moment(msg.scheduleTime, "YYYY-MM-DD").format("MMM D, YYYY")}
          time={moment(msg.scheduleTime).local().format("h:mm A")}
          phoneNumber={msg.number}
          msg={msg}
          onDelete={showchatDeleteModal}
          previouslySelectedDate={msg.scheduleTime}
          scrollToMessage={scrollToMessage}
        />
      ) : (
        <Flex vertical gap={6}>
          {!isSelectionMode && (
            <div className="msg-actions">
              <MsgActionsMenu
                type={isUser ? "user" : "others"}
                setReplyingTo={setReplyingTo}
                showchatDeleteModal={showchatDeleteModal}
                message={msg}
              />
            </div>
          )}

          {msg?.isForwarded && (
            <Flex align="center" gap={4} className="forwarded-label">
              <img
                src="/assets/icons/forwarded-message.svg"
                alt=""
                width={15}
                height={15}
                className="forward-icon"
              />
              <span>Forwarded</span>
            </Flex>
          )}

          {msg?.replyMessage !== null && msg.replyMessage !== undefined && (
            <div
              className={`reply-parent ${isUser ? "user" : ""}`}
              onClick={() => {
                if (msg.replyToId && scrollToMessage) {
                  scrollToMessage(msg.replyToId);
                }
              }}
              style={{
                cursor: msg.replyToId ? "pointer" : "default",
              }}
            >
              {msg?.replyMediaType === 0 ? (
                <Text className="text">
                  {msg?.replyMessage || msg?.filePath}
                </Text>
              ) : msg?.replyMediaType === 1 && msg?.replyFilePath ? (
                <Image
                  src={msg.replyFilePath}
                  preview={true}
                  alt="chat image"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : msg?.replyMediaType === 2 && msg?.replyFilePath ? (
                <video
                  controls
                  style={{
                    maxWidth: "100%",
                    maxHeight: "120px",
                    borderRadius: "8px",
                    display: "block",
                  }}
                >
                  <source src={msg.replyFilePath} />
                </video>
              ) : msg?.replyMediaType === 4 && msg?.replyFilePath ? (
                <AudioPlayer
                  audioUrl={msg?.replyFilePath}
                  isUserMessage={false}
                  messageId={`reply-${msg.id}`}
                />
              ) : (msg?.replyMediaType === 2 || msg?.replyMediaType === 3) &&
                msg?.replyFilePath ? (
                <Flex align="center" gap={10}>
                  <FileOutlined className="file-icon" />
                  <a
                    className="file-link"
                    href={msg?.replyFilePath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open File
                  </a>
                </Flex>
              ) : null}
            </div>
          )}

          {msg.mediaType === 1 && msg.filePath ? (
            <div
              className="media-wrapper"
              onClick={() => !isUploading && !isUploadFailed && onMediaClick?.(msg.id)}
              style={{ cursor: !isUploading && !isUploadFailed && onMediaClick ? "pointer" : "default" }}
            >
              <Image
                src={msg.filePath}
                alt="chat image"
                preview={false}
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  borderRadius: "8px",
                  objectFit: "cover",
                  opacity: isUploading || isUploadFailed ? 0.5 : 1,
                }}
              />
              {(isUploading || isUploadFailed) && msg.uploadTaskId && (
                <UploadOverlay
                  taskId={msg.uploadTaskId}
                  status={upload.status || "uploading"}
                  progress={upload.progress}
                />
              )}
            </div>
          ) : msg.mediaType === 4 && msg.filePath ? (
            <Flex vertical gap={4} className="audio-bubble-wrapper">
              <AudioPlayer
                audioUrl={msg?.filePath}
                isUserMessage={isUser}
                messageId={String(msg.id)}
                disabled={!!isUploading || !!isUploadFailed}
                isProcessing={false}
              />
              {(isUploading || isUploadFailed) && msg.uploadTaskId && (
                <Flex justify="flex-end">
                  <AudioUploadIndicator
                    taskId={msg.uploadTaskId}
                    status={upload.status || "uploading"}
                    progress={upload.progress}
                  />
                </Flex>
              )}
            </Flex>
          ) : msg.mediaType === 2 && msg.filePath ? (
            <div className="media-wrapper video-thumb-wrapper">
              {/* Thumbnail — first frame, no controls */}
              <video
                src={msg.filePath}
                preload="metadata"
                muted
                playsInline
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  borderRadius: "8px",
                  display: "block",
                  opacity: isUploading || isUploadFailed ? 0.5 : 1,
                }}
              />
              {/* Play button overlay — opens MediaViewer */}
              {!isUploading && !isUploadFailed && (
                <button
                  className="video-play-overlay"
                  onClick={() => onMediaClick?.(msg.id)}
                  aria-label="Play video"
                >
                  <span className="video-play-icon" />
                </button>
              )}
              {(isUploading || isUploadFailed) && msg.uploadTaskId && (
                <UploadOverlay
                  taskId={msg.uploadTaskId}
                  status={upload.status || "uploading"}
                  progress={upload.progress}
                />
              )}
            </div>
          ) : msg.mediaType === 3 && msg.filePath ? (
            <div
              className="media-wrapper"
              onClick={() => onMediaClick?.(msg.id)}
              style={{ cursor: onMediaClick ? "pointer" : "default" }}
            >
              <Flex align="center" gap={10}>
                <FileOutlined className="file-icon" />
                <a
                  className="file-link"
                  href={msg.filePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open File
                </a>
              </Flex>
              {(isUploading || isUploadFailed) && msg.uploadTaskId && (
                <UploadOverlay
                  taskId={msg.uploadTaskId}
                  status={upload.status || "uploading"}
                  progress={upload.progress}
                />
              )}
            </div>
          ) : msg.isPoll && msg.pollDetails ? (
            <PollBubble
              msgId={msg.id}
              isUser={isUser}
              question={msg.message}
              multiple={msg.pollDetails.multiAllow}
              options={msg.pollDetails.options}
              timestamp={msg.timestamp}
            />
          ) : msg.isTempMsg && msg.uploadTaskId && msg.mediaType === 4 ? (
            <Flex vertical gap={4} className="audio-bubble-wrapper">
              <AudioPlayer
                audioUrl=""
                isUserMessage={isUser}
                messageId={String(msg.id)}
                disabled
                isProcessing
              />
              <Flex justify="flex-end">
                <AudioUploadIndicator
                  taskId={msg.uploadTaskId}
                  status={upload.status || "uploading"}
                  progress={upload.progress}
                />
              </Flex>
            </Flex>
          ) : msg.isTempMsg && msg.uploadTaskId ? (
            <div className="media-wrapper">
              {msg.message && <Text className="text-msg">{msg.message}</Text>}
              <UploadOverlay
                taskId={msg.uploadTaskId}
                status={upload.status || "uploading"}
                progress={upload.progress}
              />
            </div>
          ) : msg.isTempMsg ? (
            <Text className="text-msg">{msg.message}</Text>
          ) : msg.message ? (
            <Text className="text-msg">
              <EmojiRenderer text={msg.message} />
            </Text>
          ) : null}

          <Flex justify="space-between" align="center" gap={40}>
            <Text className={`footer-text`}>
              {msg?.scheduleTime
                ? formatTimestamp(msg?.scheduleTime)
                : msg.timestamp}
            </Text>
            <Text className={`footer-text number`}>
              {msg.isUser
                ? msg.senderName || storageService.getFullName() || "Me"
                : msg.senderName ||
                  checkIfContactSaved(msg?.number, contacts) ||
                  (!currentChat?.isGroup && !currentChat?.isBroadCast
                    ? currentChat?.savedName || currentChat?.number
                    : undefined)}
            </Text>
          </Flex>
        </Flex>
      )}
    </Card>
  );
};

export default memo(ChatBubble);
